/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { InputAdornment } from "@mui/material";
import { ChangeEvent, FC, FocusEvent } from "react";
import { Control, Controller } from "react-hook-form";
import { useSelector } from "react-redux";

import { usePermission } from "../../../hooks/usePermission";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import TestIds from "../../../testIds";
import {
  calculateDiscountPrice,
  formatPriceInput,
  isValidCurrencyAmount,
  isValidPriceInput,
} from "../../../util/formatting";
import InputFormControl from "../../form-controls/InputFormControl/InputFormControl";

import { EUnitPriceFields, TUnitPriceFormData } from "./types";

export interface IFormControlUnitPriceProps {
  control: Control<TUnitPriceFormData>;
  validateField: (errorMessage: string) => (value?: string) => string | true;
  clearError: () => void;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  originalPrice?: number;
  setValue: (value: string) => void;
}

export const FormControlUnitPrice: FC<IFormControlUnitPriceProps> = ({
  control,
  validateField,
  clearError,
  onChange,
  onFocus,
  originalPrice,
  setValue,
}) => {
  const remoteConfig = useSelector(selectRemoteConfig);
  const noDiscountThresholdApplies = usePermission(
    "StaffSpecific_Pricing_NoDiscountThresholdApplies"
  ).allowed;
  const validateUnitPrice = (value?: string): string | true => {
    const errorMessage = "Please enter a valid unit price";
    if (!isValidCurrencyAmount(value?.trim() || "")) {
      return errorMessage;
    }

    const unitPrice = parseFloat(value || "0");
    if (unitPrice <= 0) {
      return "Please enter a unit price greater than $0";
    }

    const maxDiscountPrice =
      calculateDiscountPrice(originalPrice, remoteConfig.discountPercentageThreshold) ?? 0;
    if (!noDiscountThresholdApplies && originalPrice && unitPrice < maxDiscountPrice) {
      setValue(maxDiscountPrice.toString());
      return `Price cannot be reduced below $${maxDiscountPrice.toString()}`;
    }

    return validateField(errorMessage)(value);
  };

  return (
    <Controller
      name={EUnitPriceFields.UnitPrice}
      control={control}
      rules={{ validate: validateUnitPrice }}
      render={({ field, fieldState: { error } }) => (
        <InputFormControl
          id={EUnitPriceFields.UnitPrice}
          data-testid={TestIds.components.forms.unitPriceForm.unitPriceField}
          variant="farmlands2.0_default"
          labelColor="secondary"
          type="text"
          inputMode="decimal"
          label="Unit price (GST excl.)"
          placeholder={field.value}
          error={!!error}
          required
          value={field.value}
          errorMessage={error?.message}
          startAdornment={<InputAdornment position="start">$</InputAdornment>}
          inputProps={{
            autoComplete: "off",
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value;

              if (isValidPriceInput(value)) {
                field.onChange(value);
                onChange?.(value);
                if (error) {
                  clearError();
                }
              }
            },
            onBlur: (event: FocusEvent<HTMLInputElement>) => {
              const formattedValue = formatPriceInput(event.target.value);

              if (formattedValue) {
                field.onChange(formattedValue);
              }

              field.onBlur();
            },
            onFocus: onFocus,
          }}
        />
      )}
    />
  );
};
